import React from 'react';
import { Navigate } from 'react-router-dom';
import { isEmpty } from 'lodash';

import {
  Status,
  useAuthContext,
  useSubscriptionContext,
  OnboardingStepKey,
  onboardingSteps,
  getFirstOnboardingStepsAfterPaywall,
  getLastOnboardingStepAfterPaywall,
} from '../data-access';
import { LoadingOverlay } from '../components';
import { ONBOARDING_PATH, Path } from './path';
import { OnboardingStatus } from '@demind-inc/core';
import { useFeatureFlag } from '../utils';

const hasCompletedStep = (status?: string) => ['answered', 'skipped'].includes(status ?? '');

export const LoginContainer: React.FC = () => {
  const { status, user, isUserFetching } = useAuthContext();
  const { isValidating, hasAccessToApp, hasNoSubscriptionsTrial } = useSubscriptionContext();

  if (status === Status.UN_AUTHORIZED) {
    return <Navigate to={ONBOARDING_PATH.WELCOME} replace />;
  }

  if (status === Status.LOADING || isUserFetching || isEmpty(user) || isValidating) {
    return <LoadingOverlay />;
  }

  const hasCompletedBeforePaywall = !!user.onboarding?.beforePaywall;
  if (!hasCompletedBeforePaywall) {
    const firstStepAfterSignIn =
      onboardingSteps.find((step) => step.afterSignIn)?.key || 'MANUAL_SLEEP_INPUT';
    return <Navigate to={ONBOARDING_PATH[firstStepAfterSignIn]} replace />;
  }

  if (!hasAccessToApp) {
    return <Navigate to={Path.PAYWALL} replace />;
  }

  const hasCompletedAfterPaywall = !!user.onboarding?.afterPaywall;
  if (!hasCompletedAfterPaywall) {
    const afterPaywallStep =
      getLastOnboardingStepAfterPaywall() ||
      getFirstOnboardingStepsAfterPaywall() ||
      'PERSONA_SURVEY';
    return <Navigate to={ONBOARDING_PATH[afterPaywallStep as OnboardingStepKey]} replace />;
  }

  return <Navigate to={Path.DASHBOARD} replace />;
};
